import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useState, useRef } from 'react';
import * as yup from 'yup';
import {
  Bell,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Mail,
  Link,
  Gamepad2,
  AlertCircle,
  Zap,
  Users,
} from 'lucide-react';

import { Button } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { Input, Select } from '@@/form-components/Input';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { Modal } from '@@/modals';
import { Alert } from '@@/Alert';
import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';
import { isValidUrl } from '@@/form-components/validate-url';

import {
  AlertSettingsRow,
  NotificationChannel,
  NotificationChannelType,
} from './types';
import { useUpdateAlertSettings } from './useAlertSettings';

interface Props {
  alertSettings: AlertSettingsRow;
  onSave: () => void;
  onCancel: () => void;
}

interface FormValues {
  name: string;
  enabled: boolean;
  notificationChannels: NotificationChannel[];
}

const notificationChannelTypes = [
  { value: 'slack', label: 'Slack' },
  { value: 'email', label: 'Email' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'msteamsv2', label: 'Microsoft Teams V2' },
];

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  enabled: yup.boolean().required(),
  notificationChannels: yup.array().of(
    yup.object({
      name: yup.string().required('Channel name is required'),
      type: yup.string().required('Channel type is required'),
      config: yup.object().when('type', {
        is: 'slack',
        then: (schema) =>
          schema.shape({
            name: yup.string().required('Name is required'),
            api_url: yup
              .string()
              .required('Webhook URL is required')
              .test('valid-url', 'Invalid URL format', (value) =>
                value ? isValidUrl(value) : false
              ),
          }),
        otherwise: (schema) =>
          schema.when('type', {
            is: 'webhook',
            then: (schema) =>
              schema.shape({
                name: yup.string().required('Name is required'),
                url: yup
                  .string()
                  .required('Webhook URL is required')
                  .test('valid-url', 'Invalid URL format', (value) =>
                    value ? isValidUrl(value) : false
                  ),
              }),
            otherwise: (schema) =>
              schema.when('type', {
                is: 'msteamsv2',
                then: (schema) =>
                  schema.shape({
                    name: yup.string().required('Name is required'),
                    webhook_url: yup
                      .string()
                      .required('Webhook URL is required')
                      .test('valid-url', 'Invalid URL format', (value) =>
                        value ? isValidUrl(value) : false
                      ),
                  }),
                otherwise: (schema) =>
                  schema.when('type', {
                    is: 'email',
                    then: (schema) =>
                      schema.shape({
                        name: yup.string().required('Name is required'),
                        smtp_server: yup
                          .string()
                          .required('SMTP Server is required'),
                        auth_username: yup.string().optional(),
                        auth_password: yup.string().optional(),
                        email_to: yup
                          .string()
                          .required('To Email Address is required')
                          .email('Invalid email format'),
                        email_from: yup
                          .string()
                          .required('From Email Address is required')
                          .email('Invalid email format'),
                      }),
                    otherwise: (schema) =>
                      schema.shape({
                        name: yup.string().required('Name is required'),
                      }),
                  }),
              }),
          }),
      }),
    })
  ),
});

export function AlertSettingsEditForm({
  alertSettings,
  onSave,
  onCancel,
}: Props) {
  const updateAlertSettings = useUpdateAlertSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(
    new Set()
  );
  const formikRef = useRef<FormikProps<FormValues> | null>(null);

  const isInternalAlertManager =
    alertSettings.id?.toString() === '1' && alertSettings.isInternal;

  const initialValues: FormValues = {
    name: alertSettings.name,
    enabled: alertSettings.enabled,
    notificationChannels: alertSettings.notificationChannels || [],
  };

  async function handleSubmit(
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) {
    try {
      setIsSubmitting(true);
      setSubmitting(true);

      const updatedSettings = {
        ...alertSettings,
        ...values,
      };

      await updateAlertSettings.mutateAsync(updatedSettings);
      onSave();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update alert settings:', error);
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  }

  function handleSaveClick() {
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  }

  function generateChannelId() {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  function toggleChannelExpansion(channelId: string) {
    const newExpanded = new Set(expandedChannels);
    if (newExpanded.has(channelId)) {
      newExpanded.delete(channelId);
    } else {
      newExpanded.add(channelId);
    }
    setExpandedChannels(newExpanded);
  }

  function getChannelTypeIcon(type: NotificationChannelType) {
    const iconMap: Record<
      NotificationChannelType,
      React.ComponentType<{ className?: string }>
    > = {
      slack: MessageSquare,
      email: Mail,
      webhook: Link,
      discord: Gamepad2,
      pagerduty: AlertCircle,
      opsgenie: Zap,
      msteamsv2: Users,
    };
    const IconComponent = iconMap[type] || Bell;
    return <Icon icon={IconComponent} className="w-5 h-5 text-blue-500" />;
  }

  function renderConfigurationFields(
    channel: NotificationChannel,
    index: number,
    values: FormValues,
    setFieldValue: (field: string, value: unknown) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: any,
    setFieldTouched: (field: string, touched?: boolean) => void
  ) {
    function updateChannelConfig(configKey: string, value: unknown) {
      const updated = [...values.notificationChannels];
      updated[index] = {
        ...updated[index],
        config: { ...updated[index].config, [configKey]: value },
      };
      setFieldValue('notificationChannels', updated);
      // Mark the specific config field as touched to trigger validation
      setFieldTouched(
        `notificationChannels.${index}.config.${configKey}`,
        true
      );
    }

    const config = channel.config || {};
    // Ensure require_tls is always boolean for email channels
    if (channel.type === 'email' && typeof config.require_tls !== 'boolean') {
      config.require_tls = false;
    }

    switch (channel.type) {
      case 'slack':
        return (
          <div className="space-y-3">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`slack-name-${channel.id}`}
              >
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                id={`slack-name-${channel.id}`}
                value={(config.name as string) || ''}
                onChange={(e) => updateChannelConfig('name', e.target.value)}
                placeholder="Slack Alerts"
                data-cy={`slack-name-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.name && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.name}
                </div>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`slack-webhook-url-${channel.id}`}
              >
                Webhook URL <span className="text-red-500">*</span>
              </label>
              <Input
                id={`slack-webhook-url-${channel.id}`}
                value={(config.api_url as string) || ''}
                onChange={(e) => updateChannelConfig('api_url', e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
                data-cy={`slack-webhook-url-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.api_url && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.api_url}
                </div>
              )}
              <p className="text-xs text-gray-500 th-dark:text-gray-400 mt-1">
                More info about configuring Webhooks on:{' '}
                <a
                  href="https://api.slack.com/messaging/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://api.slack.com/messaging/webhooks
                </a>
              </p>
            </div>
          </div>
        );

      case 'email':
        return (
          <div className="space-y-3">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`email-name-${channel.id}`}
              >
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                id={`email-name-${channel.id}`}
                value={(config.name as string) || ''}
                onChange={(e) => updateChannelConfig('name', e.target.value)}
                placeholder="Email Alerts"
                data-cy={`email-name-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.name && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.name}
                </div>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`email-smtp-server-${channel.id}`}
              >
                SMTP Server <span className="text-red-500">*</span>
              </label>
              <Input
                id={`email-smtp-server-${channel.id}`}
                value={(config.smtp_server as string) || ''}
                onChange={(e) =>
                  updateChannelConfig('smtp_server', e.target.value)
                }
                placeholder="smtp.example.com:587"
                data-cy={`email-smtp-server-${channel.id}`}
                className="w-full"
              />
              <div className="text-xs text-gray-400 mt-1 italic">
                If port is not specified (e.g., example.com), defaults to port
                587
              </div>
              {errors?.notificationChannels?.[index]?.config?.smtp_server && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.smtp_server}
                </div>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`email-auth-username-${channel.id}`}
              >
                SMTP Auth Username
              </label>
              <Input
                id={`email-auth-username-${channel.id}`}
                value={(config.auth_username as string) || ''}
                onChange={(e) =>
                  updateChannelConfig('auth_username', e.target.value)
                }
                placeholder="user@example.com"
                data-cy={`email-auth-username-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.auth_username && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.auth_username}
                </div>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`email-auth-password-${channel.id}`}
              >
                SMTP Auth Password
              </label>
              <Input
                id={`email-auth-password-${channel.id}`}
                type="password"
                value={(config.auth_password as string) || ''}
                onChange={(e) =>
                  updateChannelConfig('auth_password', e.target.value)
                }
                placeholder="password"
                data-cy={`email-auth-password-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.auth_password && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.auth_password}
                </div>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`email-to-${channel.id}`}
              >
                Require TLS
              </label>
              <Switch
                id={`email-require-tls-${channel.id}`}
                checked={Boolean(config.require_tls)}
                onChange={(checked) =>
                  updateChannelConfig('require_tls', !!checked)
                }
                name={`email-require-tls-${channel.id}`}
                data-cy={`email-require-tls-${channel.id}`}
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`email-to-${channel.id}`}
              >
                To Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                id={`email-to-${channel.id}`}
                value={(config.email_to as string) || ''}
                onChange={(e) =>
                  updateChannelConfig('email_to', e.target.value)
                }
                placeholder="alerts@example.com"
                data-cy={`email-to-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.email_to && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.email_to}
                </div>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`email-from-${channel.id}`}
              >
                From Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                id={`email-from-${channel.id}`}
                value={(config.email_from as string) || ''}
                onChange={(e) =>
                  updateChannelConfig('email_from', e.target.value)
                }
                placeholder="alerts@example.com"
                data-cy={`email-from-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.email_from && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.email_from}
                </div>
              )}
            </div>
          </div>
        );

      case 'webhook':
        return (
          <div className="space-y-3">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`webhook-name-${channel.id}`}
              >
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                id={`webhook-name-${channel.id}`}
                value={(config.name as string) || ''}
                onChange={(e) => updateChannelConfig('name', e.target.value)}
                placeholder="Webhook Alerts"
                data-cy={`webhook-name-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.name && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.name}
                </div>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`webhook-url-${channel.id}`}
              >
                Webhook URL <span className="text-red-500">*</span>
              </label>
              <Input
                id={`webhook-url-${channel.id}`}
                value={(config.url as string) || ''}
                onChange={(e) => updateChannelConfig('url', e.target.value)}
                placeholder="https://example.com/webhook"
                data-cy={`webhook-url-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.url && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.url}
                </div>
              )}
            </div>
          </div>
        );

      case 'msteamsv2':
        return (
          <div className="space-y-3">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`msteams-name-${channel.id}`}
              >
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                id={`msteams-name-${channel.id}`}
                value={(config.name as string) || ''}
                onChange={(e) => updateChannelConfig('name', e.target.value)}
                placeholder="Microsoft Teams Alerts"
                data-cy={`msteams-name-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.name && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.name}
                </div>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--text-body-color)' }}
                htmlFor={`msteams-webhook-url-${channel.id}`}
              >
                Webhook URL <span className="text-red-500">*</span>
              </label>
              <Input
                id={`msteams-webhook-url-${channel.id}`}
                value={(config.webhook_url as string) || ''}
                onChange={(e) =>
                  updateChannelConfig('webhook_url', e.target.value)
                }
                placeholder="https://outlook.office.com/webhook/..."
                data-cy={`msteams-webhook-${channel.id}`}
                className="w-full"
              />
              {errors?.notificationChannels?.[index]?.config?.webhook_url && (
                <div className="text-error-9 th-dark:text-error-7 text-xs mt-1 font-medium">
                  {errors.notificationChannels[index].config.webhook_url}
                </div>
              )}
              <p className="text-xs text-gray-500 th-dark:text-gray-400 mt-1">
                More info about configuring Microsoft Teams (New Teams)
                Webhooks:{' '}
                <a
                  href="https://docs.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://docs.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook
                </a>
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div
            className="text-sm p-3 rounded border-l-4 border-blue-400"
            style={{
              backgroundColor: 'var(--bg-body-color)',
              color: 'var(--text-muted-color)',
            }}
          >
            <div className="flex items-start">
              <div className="text-blue-400 mr-2">ℹ️</div>
              <div>
                Configuration fields for {channel.type} will be available once
                this channel type is fully implemented.
              </div>
            </div>
          </div>
        );
    }
  }

  return (
    <>
      <Modal.Header title="Edit Alert Settings" />

      <Modal.Body>
        {isInternalAlertManager && (
          <Alert color="info" className="mb-4">
            This is the internal alert manager. The name cannot be modified.
          </Alert>
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnChange
          validateOnBlur
          innerRef={formikRef}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form>
              <div className="form-group">
                <FormControl
                  label="Name"
                  inputId="name-input"
                  errors={errors.name && touched.name ? errors.name : undefined}
                  required
                  tooltip={
                    isInternalAlertManager
                      ? 'Internal alert manager name cannot be changed'
                      : undefined
                  }
                >
                  <Input
                    id="name-input"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter alert manager name"
                    data-cy="alert-settings-name-input"
                    readOnly={isInternalAlertManager}
                    disabled={isInternalAlertManager}
                  />
                </FormControl>
              </div>

              <div className="form-group">
                <FormControl label="Enabled" inputId="enabled-input">
                  <Switch
                    id="enabled-input"
                    name="enabled"
                    checked={values.enabled}
                    onChange={(checked) => setFieldValue('enabled', checked)}
                    data-cy="alert-settings-enabled-switch"
                  />
                </FormControl>
              </div>

              {/* Notification Channels Management */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label
                    className="block text-sm font-medium"
                    style={{ color: 'var(--text-body-color)' }}
                  >
                    Notification Channels ({values.notificationChannels.length})
                  </label>
                  <Button
                    type="button"
                    onClick={() => {
                      const newChannel: NotificationChannel = {
                        id: generateChannelId(),
                        name: 'New Channel',
                        type: 'slack',
                        enabled: true,
                        config: {},
                      };
                      setFieldValue('notificationChannels', [
                        ...values.notificationChannels,
                        newChannel,
                      ]);
                      // Auto-expand new channel
                      const newExpanded = new Set(expandedChannels);
                      newExpanded.add(newChannel.id);
                      setExpandedChannels(newExpanded);
                    }}
                    icon={Plus}
                    color="primary"
                    size="small"
                    data-cy="add-notification-channel"
                  >
                    Add Channel
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1 mb-4">
                  More notification channels will be added over time.
                </p>

                {values.notificationChannels.length === 0 ? (
                  <div
                    className="flex flex-col items-center justify-center py-12 px-8 border-2 border-dashed border-gray-200 th-dark:border-gray-700 rounded-xl"
                    style={{ backgroundColor: 'var(--bg-widget-color)' }}
                  >
                    <h3
                      className="text-lg font-medium mb-2"
                      style={{ color: 'var(--text-body-color)' }}
                    >
                      No notification channels configured
                    </h3>
                    <p className="text-gray-500 th-dark:text-gray-400 text-center mb-4 max-w-xs">
                      Add notification channels to receive alerts when issues
                      are detected in your environment.
                    </p>
                    <Button
                      type="button"
                      onClick={() => {
                        const newChannel: NotificationChannel = {
                          id: generateChannelId(),
                          name: 'New Channel',
                          type: 'slack',
                          enabled: true,
                          config: {},
                        };
                        setFieldValue('notificationChannels', [
                          ...values.notificationChannels,
                          newChannel,
                        ]);
                        // Auto-expand new channel
                        const newExpanded = new Set(expandedChannels);
                        newExpanded.add(newChannel.id);
                        setExpandedChannels(newExpanded);
                      }}
                      icon={Plus}
                      color="primary"
                      size="small"
                      data-cy="add-first-notification-channel"
                    >
                      Add Your First Channel
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {values.notificationChannels.map((channel, index) => {
                      const isExpanded = expandedChannels.has(channel.id);
                      return (
                        <div
                          key={channel.id}
                          className="border border-gray-200 th-dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                          style={{ backgroundColor: 'var(--bg-widget-color)' }}
                        >
                          {/* Channel Header */}
                          <div className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 flex-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleChannelExpansion(channel.id)
                                  }
                                  className="flex-shrink-0 p-1 rounded hover:bg-gray-100 th-dark:hover:bg-gray-700 transition-colors"
                                  data-cy={`expand-channel-${channel.id}`}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-400 th-dark:text-gray-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-400 th-dark:text-gray-500" />
                                  )}
                                </button>

                                <div className="flex items-center space-x-3 flex-1">
                                  <div className="flex-shrink-0">
                                    {getChannelTypeIcon(channel.type)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <h4
                                        className="text-sm font-medium truncate"
                                        style={{
                                          color: 'var(--text-body-color)',
                                        }}
                                      >
                                        {channel.config?.name || channel.name}
                                      </h4>
                                      <Badge
                                        type="info"
                                        className="flex-shrink-0"
                                      >
                                        {notificationChannelTypes.find(
                                          (t) => t.value === channel.type
                                        )?.label || channel.type}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center space-x-3 flex-shrink-0 ml-4">
                                {/* Enable/Disable Toggle */}
                                <div className="flex items-center">
                                  <Switch
                                    id={`channel-enabled-${channel.id}`}
                                    name={`channel-enabled-${channel.id}`}
                                    checked={channel.enabled ?? true}
                                    onChange={(enabled) => {
                                      const updated = [
                                        ...values.notificationChannels,
                                      ];
                                      updated[index] = {
                                        ...updated[index],
                                        enabled,
                                      };
                                      setFieldValue(
                                        'notificationChannels',
                                        updated
                                      );
                                    }}
                                    data-cy={`channel-enabled-${channel.id}`}
                                  />
                                </div>

                                {/* Delete Button */}
                                <Button
                                  type="button"
                                  onClick={() => {
                                    const updated =
                                      values.notificationChannels.filter(
                                        (_, i) => i !== index
                                      );
                                    setFieldValue(
                                      'notificationChannels',
                                      updated
                                    );
                                    // Remove from expanded set
                                    const newExpanded = new Set(
                                      expandedChannels
                                    );
                                    newExpanded.delete(channel.id);
                                    setExpandedChannels(newExpanded);
                                  }}
                                  color="dangerlight"
                                  size="small"
                                  icon={Trash2}
                                  data-cy={`delete-channel-${channel.id}`}
                                  className="!p-2"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Channel Configuration (Expandable) */}
                          {isExpanded && (
                            <div
                              className="border-t border-gray-100 th-dark:border-gray-700 p-4"
                              style={{
                                backgroundColor: 'var(--bg-body-color)',
                              }}
                            >
                              <div className="space-y-4">
                                <div>
                                  <label
                                    htmlFor={`channel-type-${channel.id}`}
                                    className="block text-sm font-medium mb-2"
                                    style={{ color: 'var(--text-body-color)' }}
                                  >
                                    Notification Type{' '}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <Select
                                    id={`channel-type-${channel.id}`}
                                    value={channel.type}
                                    onChange={(e) => {
                                      const updated = [
                                        ...values.notificationChannels,
                                      ];
                                      updated[index] = {
                                        ...updated[index],
                                        type: e.target
                                          .value as NotificationChannelType,
                                        name: e.target.value, // Auto-set name to type
                                        config: {}, // Reset config when type changes
                                      };
                                      setFieldValue(
                                        'notificationChannels',
                                        updated
                                      );
                                    }}
                                    options={notificationChannelTypes}
                                    data-cy={`channel-type-select-${channel.id}`}
                                    className="w-full"
                                  />
                                </div>

                                {/* Configuration Fields */}
                                <div>
                                  <h5
                                    className="text-sm font-medium mb-3"
                                    style={{ color: 'var(--text-body-color)' }}
                                  >
                                    Configuration
                                  </h5>
                                  {renderConfigurationFields(
                                    channel,
                                    index,
                                    values,
                                    setFieldValue,
                                    errors,
                                    setFieldTouched
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="button"
          color="default"
          onClick={onCancel}
          data-cy="alert-settings-cancel-button"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={isSubmitting}
          data-cy="alert-settings-save-button"
          onClick={handleSaveClick}
        >
          {isSubmitting ? 'Saving...' : 'Save Settings'}
        </Button>
      </Modal.Footer>
    </>
  );
}
